import type {PagedResponse} from '@/ts/types/dto/pagination-dto';
import type {FetchWrapper} from '@/ts/api/fetch-wrapper';
import type {
  ReccoCreateQuestionnaireAnswerDTO,
  ReccoQuestionnaireResponse,
  ReccoNextAvailableQuestionnaireResponse} from '@/ts/types/dto/recco/questionnaire.dto';
import {getNulledUuid} from '@/ts/utils/pure-functions';
import type {ReccoTopicDTO} from '@/ts/types/dto/recco/topic.dto';
import type {ReccoArticleDTO} from '@/ts/types/dto/recco/article.dto';
import type {ReccoAudioDTO} from '@/ts/types/dto/recco/audio.dto';
import type {ReccoVideoDTO} from '@/ts/types/dto/recco/video.dto';
import type {ChallengeDTO, ChallengeRecommendationDTO} from '@/ts/types/dto/recco/challenge';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import type {
  ExploreLibraryFilters,
  ExploreLibraryPage,
  ExploreLibraryItemDTO,
  ExploreLibraryPartnerDTO} from '@/ts/types/dto/recco/library.dto';
import type {GetBadgesResponse} from '@/ts/types/dto/badge-dto';

/** `/block-content/bookmarked` caps `size` at 100 but IS paged, so a longer list is fetched page by page. */
const BOOKMARKED_PAGE_SIZE = 100;

/** Provides access to content endpoints: questionnaires, recommendations, articles, challenges, and badges */
export class ContentApi {
  public constructor(private readonly fetch: FetchWrapper) {}

  public async getQuestionnaireById(id: string): Promise<ReccoQuestionnaireResponse> {
    const questionnaire = await this.fetch.get<{questions: ReccoQuestionnaireResponse}>({
      url: `/questionnaire/${id}`,
    });
    return questionnaire.questions;
  }

  public async getNextAvailableQuestionnaire(): Promise<ReccoNextAvailableQuestionnaireResponse|null> {
    return this.fetch.get({
      url: '/recommendations/questionnaires/next-by-topic',
      handle404: true,
    });
  }

  public async postQuestionnaireAnswers(body: ReccoCreateQuestionnaireAnswerDTO[]): Promise<void> {
    const somethingThatLooksLikeAnUuidToFoolTheEndpointValidation = getNulledUuid();
    const validBody = body.map((bodyItem): unknown => {
      return {
        questionId: somethingThatLooksLikeAnUuidToFoolTheEndpointValidation,
        questionnaireId: somethingThatLooksLikeAnUuidToFoolTheEndpointValidation,
        ...bodyItem,
      };
    });

    return this.fetch.post({
      url: '/questionnaire',
      body: validBody,
    });
  }

  /** The unfiltered recommendation band. Explore's hero is the only caller. */
  public async getRecommendations(): Promise<ComponentContent[]> {
    return this.fetch.get({
      url: '/recommendations/content',
      queryParams: {size: 11},
    });
  }

  public async getTopics(): Promise<ReccoTopicDTO[]> {
    return this.fetch.get({
      url: '/block-content/topics',
    });
  }

  public async getArticle(catalogId: string): Promise<ReccoArticleDTO> {
    return this.fetch.get({
      url: '/challenges/content/article',
      queryParams: {
        catalogId,
      },
    });
  }

  public async getAudio(catalogId: string): Promise<ReccoAudioDTO> {
    return this.fetch.get({
      url: '/challenges/content/audio',
      queryParams: {
        catalogId,
      },
    });
  }

  public async getVideo(catalogId: string): Promise<ReccoVideoDTO> {
    return this.fetch.get({
      url: '/challenges/content/video',
      queryParams: {
        catalogId,
      },
    });
  }

  private async getBookmarkPage(page: number): Promise<{content: ComponentContent[]; totalElements: number}> {
    return this.fetch.get({
      url: `/block-content/bookmarked?page=${page}&size=${BOOKMARKED_PAGE_SIZE}`,
    });
  }

  /** Every page, not just the first: asking for one page of 100 dropped the tail of a longer list. */
  public async getBookmarks(): Promise<ComponentContent[]> {
    const first = await this.getBookmarkPage(0);
    const pageCount = Math.ceil(first.totalElements / BOOKMARKED_PAGE_SIZE);
    if (pageCount <= 1) {
      return first.content;
    }
    const rest = await Promise.all(
      Array.from({length: pageCount - 1}, async(_, index) => this.getBookmarkPage(index + 1)),
    );
    return [first.content, ...rest.map((response) => response.content)].flat();
  }

  /** Tag-matched challenge recommendations (max 3, BE-capped) mixed into the "For you" rail. */
  public async getRecommendedChallenges(): Promise<ChallengeRecommendationDTO[]> {
    return this.fetch.get({
      url: '/recommendations/challenges',
    });
  }

  /**
   * One page of the Explore library, filtered by facets. OR within a facet, AND across facets —
   * resolved server-side. Only active facets are sent; empty sets are omitted. Sets are passed
   * comma-joined so Spring binds them to `Set<>` params (e.g. `?mediaTypes=watch,listen`);
   * lowercase is fine, the backend binds enums case-insensitively.
   *
   * `totalElements` is the number of items the facets matched before the page window, so counts
   * come from it and never from `items.length`, which is bounded by `size` (IPD-3065). `page`
   * echoes the page served, which is what a caller should derive its next index from.
   */
  public async getExploreLibrary(
    filters: ExploreLibraryFilters,
    size: number,
    page = 0,
  ): Promise<ExploreLibraryPage> {
    const queryParams: Record<string, string> = {size: String(size), page: String(page)};
    if (filters.mediaTypes.length > 0) {
      queryParams.mediaTypes = filters.mediaTypes.join(',');
    }
    if (filters.topics.length > 0) {
      queryParams.topics = filters.topics.join(',');
    }
    if (filters.contentTypes.length > 0) {
      queryParams.contentTypes = filters.contentTypes.join(',');
    }
    if (filters.saved) {
      queryParams.saved = 'true';
    }
    if (filters.recommended) {
      queryParams.recommended = 'true';
    }
    if (filters.partnerIds.length > 0) {
      queryParams.partnerIds = filters.partnerIds.join(',');
    }
    const data = await this.fetch.get<PagedResponse<ExploreLibraryItemDTO>>({
      url: '/explore/library/paged',
      queryParams,
    });

    return {items: data.content, totalElements: data.totalElements, page: data.page};
  }

  /** Partners with content available to the user's org — feeds the "by {Partner}" chips. Empty when none. */
  public async getExplorePartners(): Promise<ExploreLibraryPartnerDTO[]> {
    return this.fetch.get({
      url: '/explore/partners',
    });
  }

  public async getChallenge(contentId: number): Promise<ChallengeDTO> {
    return this.fetch.get({
      url: `/challenges/${contentId}`,
    });
  }

  public async activateChallenge(contentId: number): Promise<ChallengeDTO> {
    return this.fetch.post({
      url: `/challenges/${contentId}`,
    });
  }

  public async withdrawChallenge(contentId: number): Promise<void> {
    return this.fetch.delete({
      url: `/challenges/${contentId}`,
    });
  }

  public async flagChallengeTaskAsDone(taskId: string): Promise<void> {
    return this.fetch.post({
      url: `/challenges/tasks/${taskId}`,
    });
  }

  public async getBadges(): Promise<GetBadgesResponse> {
    return this.fetch.get({
      url: '/challenges/badges',
    });
  }
}

/**
 * The largest `size` `/explore/library/paged` accepts, and a hard bound on the items returned.
 * Callers that want the whole set in one request ask for this; callers that page use a page size.
 *
 * Nothing checks whether the window cut the result. Neither of its callers pages — Explore's
 * filtered view and the partner band — so a selection matching more than 250 renders the first 250
 * while its count reports the full match: the count is the size of the matching set, not of what is
 * on screen, and topic rails already send the reader to "View all" for the rest. Not reachable with
 * today's catalog (an org sees at most the shared pack plus its own), so this is a note for
 * whoever hits it first, not a guard that exists.
 */
export const EXPLORE_LIBRARY_MAX_SIZE = 250;

/** Page size for the paged all-results views (topic grid). */
export const EXPLORE_LIBRARY_PAGE_SIZE = 20;
